import React, { useEffect, useRef, useState, useCallback } from "react";
import { XMarkIcon, PhotoIcon } from "@heroicons/react/24/outline";
import axios from "axios";

export default function ArchiveVisionModal({ vision, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [previewBroken, setPreviewBroken] = useState(false);
  const fileInputRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !uploading && !sending) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, uploading, sending]);

  const onOverlayClick = (e) => {
    if (e.target === overlayRef.current && !uploading && !sending) {
      onClose?.();
    }
  };

  const handleFilePick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setUploading(true);
    setPreviewBroken(false);

    try {
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
        throw new Error("Only image or video files are allowed.");
      }

      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post("/api/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });

      const url = res?.data?.url;
      if (!url) throw new Error("Upload failed. No URL returned.");
      setPhotoUrl(url);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to upload file. Please try again.";
      setError(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleConfirm = useCallback(async () => {
    setError("");
    setSending(true);
    try {
      const maybePromise = onConfirm?.({ note: note.trim(), photoUrl: photoUrl.trim() || null });
      if (maybePromise && typeof maybePromise.then === "function") {
        await maybePromise;
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to archive the vision. Please try again.";
      setError(msg);
      setSending(false);
      return;
    }
    setSending(false);
  }, [note, photoUrl, onConfirm]);

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onClick={onOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="archive-title"
    >
      <div className="surface-panel max-w-md w-full p-6 relative">
        <button
          onClick={() => !uploading && !sending && onClose?.()}
          className="btn-ghost absolute right-3 top-3 p-1 disabled:opacity-50"
          disabled={uploading || sending}
          aria-label="Close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        <h3 id="archive-title" className="text-lg font-display font-semibold text-primary mb-2">
          Archive "{vision?.title || 'Vision'}"
        </h3>
        <p className="text-sm text-secondary mb-4">
          Add a short closing note and (optionally) attach a photo or video.
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-lg border border-danger/40 bg-pine-tint text-danger text-sm">
            {error}
          </div>
        )}

        <label className="block text-sm text-primary mb-1">Closing note</label>
        <textarea
          className="input-editorial mb-4 min-h-[90px] resize-none"
          placeholder="How does it feel to finish this?"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={1000}
        />

        <label className="block text-sm text-primary mb-1">Photo/Video</label>
        <div className="flex gap-2 mb-3">
          <input
            className="input-editorial flex-1"
            placeholder="https://…"
            value={photoUrl}
            onChange={(e) => {
              setPreviewBroken(false);
              setPhotoUrl(e.target.value);
            }}
            spellCheck={false}
          />
          <button
            type="button"
            onClick={handleFilePick}
            className="btn-secondary px-3 disabled:opacity-50"
            disabled={uploading || sending}
            title="Upload from device"
          >
            <div className="w-10 h-10 rounded-md bg-pine-tint flex items-center justify-center">
              <PhotoIcon className="w-5 h-5 text-pine" />
            </div>
          </button>
          <input
            type="file"
            accept="image/*,video/*"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {photoUrl && !previewBroken && photoUrl.match(/^https?:\/\//i) && (
          <div className="mb-4">
            <img
              src={photoUrl}
              alt="Attachment preview"
              className="w-full max-h-48 object-cover rounded-md border border-hairline"
              onError={() => setPreviewBroken(true)}
            />
          </div>
        )}

        {uploading && (
          <div className="mb-4 text-sm text-secondary flex items-center gap-2">
            <span className="w-4 h-4 border-2 border-hairline border-t-pine rounded-full animate-spin" />
            Uploading…
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={handleConfirm}
            className="btn-primary disabled:opacity-60"
            disabled={uploading || sending}
          >
            {sending ? "Archiving…" : "Archive"}
          </button>
          <button
            onClick={() => !uploading && !sending && onClose?.()}
            className="btn-secondary disabled:opacity-60"
            disabled={uploading || sending}
          >
            Cancel
          </button>
        </div>

        <p className="text-xs text-faint mt-3">
          Tip: You can paste a link or upload a photo/video. We'll store a copy and attach it to this achievement.
        </p>
      </div>
    </div>
  );
}
